import React, { useCallback, useRef, useState } from 'react';
import type { FingeringStorageResult } from '../../../utils/fingeringStorage';
import type { DriveTargetFolder } from '../../../utils/driveTargetFolder';
import { useGoogleDriveSaver, DriveAuthRequiredError } from '../../../hooks/useGoogleDriveSaver';
import { useToast } from '../../../components/ToastProvider';
import { copyToClipboard } from '../../../utils/clipboard';
import GoogleDriveIcon from '../../../components/icons/GoogleDriveIcon';

interface DriveLinkCopyButtonProps {
  stored: FingeringStorageResult;
  /** 探索先フォルダ。SSOT は FingeringPage の driveTarget state。null は既定 APP_FOLDER。 */
  target: DriveTargetFolder | null;
  /**
   * issue #142: Drive に運指済み PDF が保存済みか。 false のあいだはボタンを非活性にし、
   * 押下自体をさせない (旧「押してから『先に保存して』メッセージ」方式は廃止)。
   */
  savedToDrive: boolean;
  /**
   * issue #154: Drive 上に見つからない (保存済みフラグが陳腐化) ことを親へ通知する。
   * 親はフラグをクリアして「保存済み」表示を保存ボタンに戻す。
   */
  onStale?: () => void;
}

/**
 * issue #140: Drive 保存済み運指 PDF の共有リンク (webViewLink) をクリップボードへコピーする。
 * Drive の共有設定・権限は変更しない (URL を読むだけ)。
 * issue #142: 未保存 (savedToDrive=false) のあいだは disabled。 保存済みになってから押せる。
 * issue #154: 共有 URL は永続化せず都度取得する (webViewLink は安定性の保証が無く陳腐化しうる)。
 * 同一セッション内の再コピーだけ memory cache で往復を省く。
 */
const DriveLinkCopyButton: React.FC<DriveLinkCopyButtonProps> = ({ stored, target, savedToDrive, onStale }) => {
  const { getPdfShareLink, reauthenticate } = useGoogleDriveSaver();
  const { showToast } = useToast();
  const [isFetching, setIsFetching] = useState(false);
  // issue #154: セッション切れ表示 (再ログイン導線)。 取得成功で false に戻す。
  const [authRequired, setAuthRequired] = useState(false);
  // issue #154: 共有 URL のセッション内メモリキャッシュ。 localStorage には絶対に置かない
  // (webViewLink は陳腐化しうるため永続化禁止)。
  const linkCacheRef = useRef<Map<string, string>>(new Map());

  const fetchAndCopy = useCallback(async () => {
    const cacheKey = `${target?.id ?? 'default'}::${stored.fileName}`;
    setIsFetching(true);
    try {
      const link = linkCacheRef.current.get(cacheKey) ?? (await getPdfShareLink(stored, target));
      if (!link) {
        // Drive 上に無い = 保存済みフラグが陳腐化 (ユーザーが削除/移動した)。
        linkCacheRef.current.delete(cacheKey);
        onStale?.();
        showToast('Drive に見つかりません。 もう一度保存してください', { kind: 'error' });
        return;
      }
      linkCacheRef.current.set(cacheKey, link);
      setAuthRequired(false);
      const copied = await copyToClipboard(link);
      showToast(
        copied ? '共有リンクをコピーしました' : 'リンクのコピーに失敗しました',
        { kind: copied ? 'success' : 'error' },
      );
    } catch (err) {
      if (err instanceof DriveAuthRequiredError) {
        setAuthRequired(true);
        showToast('ログインし直してください', { kind: 'error' });
        return;
      }
      showToast('共有リンクの取得に失敗しました', { kind: 'error' });
    } finally {
      setIsFetching(false);
    }
  }, [getPdfShareLink, stored, target, showToast, onStale]);

  // issue #154: 再ログイン → 共有リンク取得を再試行する。 popup blocker 回避のため
  // reauthenticate は onClick 同期直下で呼ぶ。
  const handleReauth = useCallback(async () => {
    try {
      await reauthenticate();
    } catch {
      showToast('ログインし直してください', { kind: 'error' });
      return;
    }
    setAuthRequired(false);
    await fetchAndCopy();
  }, [reauthenticate, fetchAndCopy, showToast]);

  const isDisabled = !savedToDrive || isFetching;

  return (
    <div data-testid="drive-link-copy-button-wrapper" className="w-full">
      <button
        type="button"
        onClick={fetchAndCopy}
        disabled={isDisabled}
        aria-label="Drive 共有リンクをコピー"
        title={savedToDrive ? undefined : 'まず Drive に保存すると押せます'}
        className="w-full group flex items-center gap-3 p-3 sm:p-3.5 bg-gradient-to-r from-sky-50 to-blue-50 border border-blue-200/60 rounded-xl hover:border-blue-300 hover:shadow-md hover:shadow-blue-100 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none disabled:hover:border-blue-200/60"
      >
        <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center flex-shrink-0 shadow-sm border border-blue-100">
          <GoogleDriveIcon className="w-5 h-5" />
        </div>
        <div className="text-left flex-1 min-w-0">
          <div className="text-sm font-bold text-gray-700">
            {isFetching ? 'リンクを取得中…' : 'Drive 共有リンクをコピー'}
          </div>
          <div className="text-xs text-gray-400 truncate">
            {savedToDrive ? '保存済み PDF の URL をコピー' : 'まず Drive に保存してください'}
          </div>
        </div>
        <svg
          className="w-5 h-5 text-gray-300 group-hover:text-blue-500 transition-colors flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
          />
        </svg>
      </button>

      {/* issue #154: セッション切れ (DriveAuthRequiredError) の再ログイン導線。 */}
      {authRequired && (
        <button
          type="button"
          onClick={handleReauth}
          aria-label="Drive に再ログイン"
          className="mt-1.5 text-xs text-blue-600 underline px-2"
        >
          ログインし直してください（再ログイン）
        </button>
      )}
    </div>
  );
};

export default DriveLinkCopyButton;
